<template>
  <div class="cfg-tab">
    <div class="cfg-intro">
      <v-icon>mdi-information-outline</v-icon>
      <div>
        <p>
          Tipurile din Echipamente și din „Demers nou”, în ordinea din listele de alegere. Circuitul de aprobare și
          inginerul se aplică doar demersurilor noi; cele depuse își păstrează circuitul.
        </p>
        <p>Un tip ascuns nu mai este oferit pentru nimic nou; echipamentele și demersurile existente rămân.</p>
      </div>
    </div>

    <div class="cfg-toolbar">
      <h3 class="panel-title">Tipuri de echipament <span class="count">{{ types.length }}</span></h3>
      <span class="grow-spacer" />
      <v-btn class="btn-green" @click="openCreate"><v-icon left>mdi-plus</v-icon>Tip nou</v-btn>
    </div>

    <v-progress-linear v-if="loading && !loaded" indeterminate color="primary" class="mb-2" />
    <div class="cfg-list">
      <div v-if="loaded && !sorted.length" class="cfg-empty">Niciun tip de echipament.</div>
      <div v-for="(t, i) in sorted" :key="t.id" class="cfg-row" :class="{ 'is-hidden': t.is_hidden }">
        <div class="cfg-order">
          <v-btn icon small :disabled="i === 0 || saving" aria-label="Mută mai sus" @click="move(t, -1)">
            <v-icon>mdi-chevron-up</v-icon>
          </v-btn>
          <v-btn icon small :disabled="i === sorted.length - 1 || saving" aria-label="Mută mai jos" @click="move(t, 1)">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>
        <div class="cfg-row__main">
          <div class="cfg-row__title">
            <span class="cfg-row__name">{{ t.type }}</span>
            <span v-if="t.is_hidden" class="cfg-tag"><v-icon>mdi-eye-off-outline</v-icon>Ascuns</span>
          </div>
          <div class="cfg-row__meta">
            <span class="cfg-tag cfg-tag--sky" :title="circuitPath(t.circuit_id)">
              <v-icon>mdi-sitemap-outline</v-icon>{{ circuitName(t.circuit_id) }}
            </span>
            <span class="cfg-tag cfg-tag--teal"><v-icon>mdi-account-wrench-outline</v-icon>{{ engineerLabel(t.engineer_role) }}</span>
          </div>
          <v-switch
            :key="`new-${t.id}-${version}`"
            :input-value="!!t.allows_new_item"
            :disabled="saving"
            inset
            dense
            hide-details
            color="primary"
            class="types__switch"
            label="Utilizatorii pot cere echipament nou"
            @change="setNewItem(t, $event)"
          ></v-switch>
          <div class="cfg-usage">{{ usageText(t.usage) || "Nefolosit încă" }}</div>
        </div>
        <div class="cfg-row__actions">
          <v-btn icon color="primary" aria-label="Editează tipul" @click="openEdit(t)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            :aria-label="t.is_hidden ? 'Arată tipul' : 'Ascunde tipul'"
            :title="t.is_hidden ? 'Arată din nou' : 'Ascunde'"
            :disabled="saving"
            @click="toggleHidden(t)"
          >
            <v-icon>{{ t.is_hidden ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
          </v-btn>
          <v-btn icon color="error" aria-label="Șterge tipul" @click="remove(t)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog v-model="dialog" persistent scrollable max-width="760" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-form class="fdlg__form" @submit.prevent="save">
          <div class="fdlg__head">
            <div>
              <h2 class="fdlg__title">{{ editing ? "Editează tipul" : "Tip nou de echipament" }}</h2>
              <p class="fdlg__sub">{{ editing ? editing.type : "Apare în Echipamente și în „Demers nou”." }}</p>
            </div>
            <v-btn icon aria-label="Închide" @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text ref="body" class="fdlg__body">
            <div v-if="editing && editing.used" class="cfg-used-note">
              <v-icon small>mdi-alert-outline</v-icon>
              <p>
                În uz: {{ usageText(editing.usage) }}. O denumire nouă se vede pe echipamente și în liste;
                demersurile depuse păstrează denumirea cu care au fost depuse.
              </p>
            </div>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-form-textbox</v-icon>Denumire</div>
              <v-text-field
                v-model="form.type"
                label="Denumirea tipului*"
                prepend-inner-icon="mdi-shape-outline"
                outlined
                dense
                maxlength="150"
                :error-messages="nameError"
                @input="nameError = ''"
              ></v-text-field>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-sitemap-outline</v-icon>Circuit de aprobare</div>
              <p class="form-section__note">
                Se aplică doar demersurilor noi; cele depuse își păstrează circuitul. O cauză își poate avea propriul
                circuit (tab-ul Cauze). Circuitele se definesc în tab-ul „Circuite aprobare”.
              </p>
              <div class="cfg-choices" role="radiogroup" aria-label="Circuit de aprobare">
                <button
                  v-for="c in circuitOptions"
                  :key="c.id"
                  type="button"
                  role="radio"
                  class="cfg-choice"
                  :class="{ 'is-active': form.circuit_id === c.id }"
                  :aria-checked="String(form.circuit_id === c.id)"
                  @click="form.circuit_id = c.id"
                >
                  <v-icon>{{ form.circuit_id === c.id ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
                  <span>
                    <span class="cfg-choice__name">{{ c.name }}<template v-if="c.is_hidden"> (ascuns)</template></span>
                    <span class="cfg-choice__hint">{{ pathText(c, engineerLabel(form.engineer_role)) }}</span>
                  </span>
                </button>
              </div>

              <div class="form-section__label types__sublabel">
                <v-icon small>mdi-account-wrench-outline</v-icon>Inginer responsabil
              </div>
              <div class="cfg-choices" role="radiogroup" aria-label="Inginer responsabil">
                <button
                  v-for="e in engineers"
                  :key="e.value"
                  type="button"
                  role="radio"
                  class="cfg-choice"
                  :class="{ 'is-active': form.engineer_role === e.value }"
                  :aria-checked="form.engineer_role === e.value"
                  @click="form.engineer_role = e.value"
                >
                  <v-icon>{{ form.engineer_role === e.value ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}</v-icon>
                  <span>
                    <span class="cfg-choice__name">{{ e.text }}</span>
                    <span class="cfg-choice__hint">{{ e.hint }}</span>
                  </span>
                </button>
              </div>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-package-variant-plus</v-icon>Echipament nou prin demers</div>
              <v-switch
                v-model="form.allows_new_item"
                inset
                color="primary"
                class="mt-0 mb-2"
                label="Utilizatorii pot cere echipament nou"
                hint="În „Demers nou” se poate cere un echipament nou de acest tip (cu o cauză „Creează echipament nou”)."
                persistent-hint
              ></v-switch>
            </section>
          </v-card-text>

          <div class="fdlg__foot">
            <v-spacer></v-spacer>
            <v-btn text large @click="dialog = false">Anulare</v-btn>
            <v-btn class="btn-green" large type="submit" :loading="saving">
              <v-icon left>mdi-content-save-outline</v-icon>Salvează
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <ConfirmUsedDialog ref="confirmUsed" />
    <RemoveDialog ref="remove" />
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import configMixin from "./configMixin";
import ConfirmUsedDialog from "./ConfirmUsedDialog.vue";
import RemoveDialog from "./RemoveDialog.vue";
import { ENGINEERS, bySortOrder, cleanText, engineerLabel, orderPatches, usageText } from "./format";
import { circuitPathText } from "@/config/circuits";

export default {
  name: "TypesTab",
  components: { ConfirmUsedDialog, RemoveDialog },
  mixins: [configMixin],
  data() {
    return {
      types: [],
      circuits: [],
      // re-keys the switches after a reload, so a refused toggle flips back
      version: 0,
      engineers: ENGINEERS,
      dialog: false,
      editing: null,
      form: {},
      nameError: "",
    };
  },
  computed: {
    sorted() {
      return [...this.types].sort(bySortOrder);
    },
    // hidden circuits only when the type already has it (an edit shows the current value)
    circuitOptions() {
      const current = this.editing ? this.editing.circuit_id : null;
      return [...this.circuits].sort(bySortOrder).filter((c) => !c.is_hidden || c.id === current);
    },
  },
  methods: {
    engineerLabel,
    usageText,
    circuitOf(id) {
      return this.circuits.find((c) => c.id === id);
    },
    circuitName(id) {
      const c = this.circuitOf(id);
      return c ? c.name : "Fără circuit";
    },
    pathText(c, engineer) {
      const path = circuitPathText(c.steps, engineer);
      return c.rector_review ? `${path} (Rectorul poate trimite „Pentru examinare” la Șeful DTI)` : path;
    },
    circuitPath(id) {
      const c = this.circuitOf(id);
      return c ? this.pathText(c) : "";
    },
    async load() {
      const [types, circuits] = await Promise.all([api.getEquipmentTypes(), api.getCircuits()]);
      this.types = types;
      this.circuits = circuits;
      this.version++;
    },
    openCreate() {
      this.editing = null;
      const first = [...this.circuits].sort(bySortOrder).find((c) => !c.is_hidden);
      this.form = { type: "", circuit_id: first ? first.id : null, engineer_role: "inginer", allows_new_item: false };
      this.openDialog();
    },
    openEdit(t) {
      this.editing = t;
      this.form = { type: t.type, circuit_id: t.circuit_id, engineer_role: t.engineer_role, allows_new_item: !!t.allows_new_item };
      this.openDialog();
    },
    openDialog() {
      this.nameError = "";
      this.dialog = true;
      helper.scrollToTop(this);
    },
    async save() {
      const name = cleanText(this.form.type);
      if (!name) {
        this.nameError = "Denumirea este obligatorie";
        return;
      }
      const t = this.editing;
      if (!t) {
        const created = await this.change(
          () => api.createEquipmentType({ ...this.form, type: name }),
          { done: `Tipul „${name}” a fost adăugat`, fallback: "Eroare la adăugarea tipului" }
        );
        if (created) this.dialog = false;
        return;
      }
      const body = {};
      if (name !== t.type) body.type = name;
      ["circuit_id", "engineer_role", "allows_new_item"].forEach((field) => {
        if (this.form[field] !== (field === "allows_new_item" ? !!t[field] : t[field])) body[field] = this.form[field];
      });
      if (!Object.keys(body).length) {
        this.dialog = false;
        return;
      }
      const saved = await this.change((extra) => api.updateEquipmentType(t.id, { ...body, ...extra }), {
        done: `Tipul „${name}” a fost salvat`,
        fallback: "Eroare la salvarea tipului",
        confirm: {
          title: `Redenumiți tipul „${t.type}”?`,
          from: t.type,
          to: name,
          typeName: true,
          changes: [
            "Echipamentele de acest tip, listele de alegere și filtrele arată denumirea nouă.",
            "Demersurile noi se depun cu denumirea nouă.",
          ],
          keeps: [
            "Demersurile depuse păstrează tipul, circuitul și inginerul cu care au fost depuse: sunt documente.",
          ],
        },
      });
      if (saved) this.dialog = false;
    },
    setNewItem(t, value) {
      this.change(() => api.updateEquipmentType(t.id, { allows_new_item: !!value }), {
        done: value ? `„${t.type}”: utilizatorii pot cere echipament nou` : `„${t.type}”: nu se mai pot cere echipamente noi`,
        fallback: "Eroare la salvarea tipului",
      });
    },
    toggleHidden(t) {
      const hide = !t.is_hidden;
      return this.change(() => api.updateEquipmentType(t.id, { is_hidden: hide }), {
        done: hide
          ? `Tipul „${t.type}” este ascuns: nu mai este oferit pentru echipamente și demersuri noi`
          : `Tipul „${t.type}” este oferit din nou`,
        fallback: "Eroare la salvarea tipului",
      });
    },
    move(t, dir) {
      const patches = orderPatches(this.sorted, t, dir);
      if (!patches.length) return;
      this.change(
        async () => {
          for (const p of patches) await api.updateEquipmentType(p.id, { sort_order: p.sort_order });
        },
        { done: "Ordinea a fost salvată", fallback: "Eroare la salvarea ordinii" }
      );
    },
    async remove(t) {
      const usage = t.usage || {};
      const canDelete = !t.used && !usage.catalogKinds;
      const choice = await this.$refs.remove.ask({
        title: canDelete ? `Ștergeți tipul „${t.type}”?` : `Tipul „${t.type}” nu poate fi șters`,
        text: canDelete
          ? "Nu are echipamente, demersuri sau catalog, deci poate fi șters."
          : `Este folosit: ${usageText(usage)}. ` +
            (t.is_hidden
              ? "Este deja ascuns, deci nu mai este oferit pentru nimic nou."
              : "Ascundeți-l: nu va mai fi oferit pentru echipamente sau demersuri noi, iar ce există rămâne neschimbat."),
        canDelete,
        canHide: !canDelete && !t.is_hidden,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteEquipmentType(t.id), {
          done: `Tipul „${t.type}” a fost șters`,
          fallback: "Eroare la ștergerea tipului",
        });
      } else if (choice === "hide") {
        await this.toggleHidden(t);
      }
    },
  },
};
</script>

<style scoped>
.types__switch {
  margin-top: 8px;
  padding-top: 0;
}
.types__switch ::v-deep .v-label {
  font-size: 0.95rem;
}
.types__sublabel {
  margin-top: 6px;
}
</style>
